import {
  ComponentProps,
  useCallback,
  useEffect,
  useState,
  useRef,
} from 'react';

import * as analytics from '@adsk/acs-web-analytics';
import {
  useCurrentProductId,
  useCurrentAccountId,
  useCurrentToolId,
  useCurrentProjectId,
} from '@adsk/alloy-provider';
import {
  DropdownMenu,
  convertGroupKeyToIndex,
} from '@adsk/alloy-react-dropdown';

import { ASSIGNEEPICKER_ANALYTICS } from '../helpers/analytics';
import { ItemWithGroup, GroupedItem } from '../types';

export const useAssigneePickerAnalytics = ({
  groupedItems,
}: {
  groupedItems: GroupedItem[];
}) => {
  const currentProjectId = useCurrentProjectId();
  const currentProductId = useCurrentProductId();
  const currentToolId = useCurrentToolId();
  const currentAccountId = useCurrentAccountId();
  const searchText = useRef<string>('');
  const selectedItems = useRef<ItemWithGroup | ItemWithGroup[]>();
  const [selectedGroup, setSelectedGroup] = useState<string>('all');
  const tagsEnabled = useRef<boolean>(true);
  const isMultiple = useRef<boolean>();

  useEffect(() => {
    if (isMultiple.current !== undefined) {
      analytics.track(ASSIGNEEPICKER_ANALYTICS.VIEW, {
        product_id: currentProductId,
        project_id: currentProjectId,
        tool_id: currentToolId,
        account_id: currentAccountId,
        single_selection: !isMultiple.current,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only want this to occur on component mount as it is the view analytic
  }, [isMultiple.current]);

  const getSetSelectedGroupKey = useCallback(
    (menuProps: ComponentProps<typeof DropdownMenu<ItemWithGroup>>) =>
      (groupKey: string) => {
        let group = 'all';
        if (groupKey) {
          const index = convertGroupKeyToIndex(groupKey);

          if (index !== null && index >= 0) {
            group = groupedItems[index].group;
          }
        }

        analytics.track(ASSIGNEEPICKER_ANALYTICS.GROUP_FILTER_APPLY, {
          product_id: currentProductId,
          project_id: currentProjectId,
          tool_id: currentToolId,
          account_id: currentAccountId,
          primary_filter: group,
          search_chars_num: searchText.current?.length ?? 0,
        });

        setSelectedGroup(group ?? 'all');
        menuProps.setSelectedGroupKey?.(groupKey);
      },
    [
      currentProductId,
      currentProjectId,
      currentToolId,
      currentAccountId,
      groupedItems,
    ],
  );

  const renderMenu = useCallback(
    (menuProps: ComponentProps<typeof DropdownMenu<ItemWithGroup>>) => {
      tagsEnabled.current = !!menuProps.allowGroupFiltering;

      const menuPropsWithSelectedItem = menuProps as ComponentProps<
        typeof DropdownMenu<ItemWithGroup>
      > & {
        selectedItem?: ItemWithGroup;
        selectedItems?: ItemWithGroup[];
      };

      isMultiple.current =
        menuPropsWithSelectedItem.selectedItems !== undefined;

      if (
        menuPropsWithSelectedItem.selectedItem ||
        menuPropsWithSelectedItem.selectedItems
      ) {
        const selectedItem = (menuPropsWithSelectedItem.selectedItem ||
          menuPropsWithSelectedItem.selectedItems) as
          | ItemWithGroup
          | ItemWithGroup[];

        if (selectedItems.current !== selectedItem) {
          let selectedItemsArray;
          if (!Array.isArray(selectedItem) && selectedItem) {
            selectedItemsArray = [selectedItem];
          } else {
            selectedItemsArray = selectedItem ?? [];
          }

          if (selectedItemsArray?.length > 0) {
            const selectedMembersCount = selectedItemsArray.filter(
              (item) => item?.group === 'user',
            ).length;

            analytics.track(ASSIGNEEPICKER_ANALYTICS.ITEM_SELECT, {
              product_id: currentProductId,
              project_id: currentProjectId,
              tool_id: currentToolId,
              account_id: currentAccountId,
              primary_filter: selectedGroup,
              search_chars_num: searchText.current?.length ?? 0,
              selected_members_no: selectedMembersCount,
              selected_category:
                selectedItemsArray[selectedItemsArray.length - 1]?.group,
              tags_enabled: tagsEnabled.current,
            });
          }
          selectedItems.current = selectedItem;
        }
      }
    },
    [
      currentProductId,
      currentProjectId,
      currentToolId,
      currentAccountId,
      selectedGroup,
    ],
  );

  return {
    searchTextRef: searchText,
    getSetSelectedGroupKey,
    renderMenuHandler: renderMenu,
  };
};
